import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../../features/authentication/stores/authStore';
import Header from '../../components/Header';
import ErrorDisplay from '../../components/ErrorDisplay';
import './MainPage.css';
import PowerBIReport from '../../components/PowerBIReport';
import MarketEditor from './components/MarketEditor/MarketEditor';
import { models, Page, Report } from 'powerbi-client';
import { Market } from './components/MarketEditor/interfaces';
import AdjustmentsEditor from './components/AdjustmentsEditor/AdjustmentsEditor';
import PaintPicker from './components/PaintPicker';
import { Adjustment } from './components/AdjustmentsEditor/interfaces';
import PDFExportButton from './components/PDFExport/Button';
import ExcelExportButton from './components/ExcelExport/Button';
import Margin from './components/Margin';
import SavePresetButton from './components/Preset/SavePresetButton';
import { generateSeries } from '../../utils';
import { Preset } from './components/Preset/models';
import OmniSlider from './components/OmniSlider';
import { TABLES, COLUMNS, VISUAL_IDS, MIN_DISCOUNT } from '../../constants/slicers';
import OverallDiscount from './components/OverallDiscount';
import PlatformFee from './components/PlatformFee/PlatformFee';
import NotesComponent from './components/NotesComponent';

const MainPage = ({ id }: { id?: string } = {}) => {
  const { accessToken, embedUrl, username, error, canDiscount, isAdminUser, isCROUser } = useAuthStore();
  const reportRef1 = useRef<Report | null>(null);
  const reportRef2 = useRef<Report | null>(null);

  const [report1Loaded, setReport1Loaded] = useState<boolean>(false);
  const [report2Loaded, setReport2Loaded] = useState<boolean>(false);

  const [companyNames, setCompanyNames] = useState<string[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [selectedCompany, setSelectedCompany] = useState('');

  const [adjustments, setAdjustments] = useState<Adjustment[]>([]);
  const [selectedSet, setSelectedSet] = useState('');

  const [paintProvided, setPaintProvided] = useState('No');
  const [unitDiscountOn, setUnitDiscountOn] = useState('No');
  const [discountDisplay, setDiscountDisplay] = useState('Overall Discount');
  const [discountSliderOrder, setDiscountSliderOrder] = useState(0);
  const [paintSliderOrder, setPaintSliderOrder] = useState(0);
  const [cleaningSliderOrder, setCleaningSliderOrder] = useState(0);
  const [carpetCleanSliderOrder, setCarpetCleanSliderOrder] = useState(0);
  const [maintenanceSliderOrder, setMaintenanceSliderOrder] = useState(0);
  const [saasFeeSliderOrder, setSaasFeeSliderOrder] = useState(250);
  const [saasContractedUnits, setSaasContractedUnits] = useState(1000);
  const [saasFeePeriod, setSaasFeePeriod] = useState<'Monthly' | 'Annually'>('Monthly');
  const [saasFeeLoading, setSaasFeeLoading] = useState(true);
  const [saasFeeTotal, setSaasFeeTotal] = useState(0);
  const [saasFeeEnabled, setSaasFeeEnabled] = useState<'Yes' | 'No'>('Yes');

  const [setNames, setSetNames] = useState<string[]>([]);

  const [margin, setMargin] = useState(0);
  const [marginLoading, setMarginLoading] = useState(true);
  const [unitDiscount, setUnitDiscount] = useState(0);
  const [overallDiscount, setOverallDiscount] = useState(0);
  const [overallDiscountLoading, setOverallDiscountLoading] = useState(true);

  const [note, setNote] = useState('');

  const [preset, setPreset] = useState<Preset>();

  const [initialSet, setInitialSet] = useState('');
  const [initialMarket, setInitialMarket] = useState('');

  const minDiscount = MIN_DISCOUNT;

  useEffect(() => {
    if (report1Loaded && report2Loaded) {
      trySetReportState();
    }
  }, [report2Loaded, report1Loaded]);

  useEffect(() => {
    if (id) {
      fetchPreset();
    }
  }, []);

  useEffect(() => {
    setMarginLoading(true);
    setOverallDiscountLoading(true);
    setSaasFeeLoading(true);
  }, [
    discountSliderOrder,
    paintProvided,
    discountDisplay,
    selectedCompany,
    selectedSet,
    unitDiscountOn,
    carpetCleanSliderOrder,
    cleaningSliderOrder,
    maintenanceSliderOrder,
    paintSliderOrder,
    saasFeeSliderOrder,
    saasContractedUnits,
    saasFeePeriod,
  ]);

  const logReportState = async () => {
    const report1 = reportRef1.current;
    const report2 = reportRef2.current;
    if (!report1 || !report2) {
      console.error('Report not found.');
      return;
    }
    try {
      const allPages = [...(await report1.getPages()), ...(await report2.getPages())];
      const activePages = allPages.filter((page: Page) => page.isActive);
      for (const page of activePages) {
        const visuals = await page.getVisuals();
        for (const visual of visuals) {
          if (visual.type === 'slicer') {
            try {
              console.log(visual.name);
              console.log(visual.title);
              console.log(await visual.exportData());
            } catch (error) {
              console.error('Error setting slicer state:', error);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error setting company:', error);
    }
  };

  const fetchPreset = async () => {
    try {
      const response = await fetch(
        `https://prod-24.eastus.logic.azure.com/workflows/b3e47c86161f426aaca526bbe407bc0d/triggers/manual/paths/invoke/${id}?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=BSWhIPqrd-4mxVxVkC8GgVoHbxz6rQ8hHjkOjlq4fDg`
      );
      const data = await response.json();

      setPreset(data[0]);

      trySetReportState();
    } catch (error) {
      console.error('Error fetching preset:', error);
    }
  };

  const trySetReportState = async () => {
    let currentPreset = preset || {
      company: '',
      set: '',
      paintProvided: 'No',
      discountToDisplay: 'Overall Discount',
      unitDiscountEnabled: 'No',
      extraDiscount: 0,
      user: username,
      paintDiscount: 0,
      cleanDiscount: 0,
      carpetCleanDiscount: 0,
      maintenanceDiscount: 0,
      saasUnitFee: 250,
      saasContractedUnits: 1000,
      saasFeePeriod: 'Monthly',
      saasFeeEnabled: 'Yes',
      reason: '',
    };

    setNote(currentPreset.reason);
    setCompany(currentPreset.company);
    updateSlider(currentPreset.extraDiscount);
    updateSet(currentPreset.set);
    updatePaint(currentPreset.paintProvided);
    updateDiscountToDisplay(currentPreset.discountToDisplay);
    updateUnitDiscountSlider(currentPreset.unitDiscountEnabled);
    updateDomainname(currentPreset.user);
    updatePaintSlider(currentPreset.paintDiscount);
    updateCleaningSlider(currentPreset.cleanDiscount);
    updateCarpetCleanSlider(currentPreset.carpetCleanDiscount);
    updateMaintenanceSlider(currentPreset.maintenanceDiscount);
    updateSaasFeeSlider(currentPreset.saasUnitFee);
    updateSaasContractedUnits(currentPreset.saasContractedUnits);
    updateSaasFeePeriod(currentPreset.saasFeePeriod);
    updateSaasFeeEnabled(currentPreset.saasFeeEnabled);
  };

  const onReportRendered = () => {
    updateMargin();
    updateUnitDiscount();
    updateOverallDiscount();
    updateSaasFeeTotal();
  };

  const updateMargin = async () => {
    if (!reportRef1.current) {
      console.error('Report container not found.');
      return;
    }

    const report = reportRef1.current;
    if (!report) {
      console.error('Report not found.');
      return;
    }

    try {
      const pages: Page[] = await report.getPages();

      let activePages = pages.filter((page: Page) => page.isActive);

      for (const page of activePages) {
        const visuals = await page.getVisuals();

        for (const visual of visuals) {
          if (visual.type === 'tableEx' || visual.type === 'card') {
            if (visual.name === VISUAL_IDS.DEAL_MARGIN) {
              let value = (await visual.exportData(models.ExportDataType.Summarized)).data;

              let dealMargin = value.match(/(-?\d+\.\d+)%/)?.[1] || '0';

              setMargin(parseFloat(dealMargin));
              setMarginLoading(false);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error exporting visual data:', error);
    }
  };

  const updateUnitDiscount = async () => {
    if (!reportRef1.current) {
      console.error('Report container not found.');
      return;
    }

    const report = reportRef1.current;
    if (!report) {
      console.error('Report not found.');
      return;
    }

    try {
      const pages: Page[] = await report.getPages();

      let activePages = pages.filter((page: Page) => page.isActive);

      for (const page of activePages) {
        const visuals = await page.getVisuals();

        for (const visual of visuals) {
          if (visual.type === 'card') {
            if (visual.name === VISUAL_IDS.UNIT_DISCOUNT) {
              let value = (await visual.exportData(models.ExportDataType.Summarized)).data;

              let discount = value.match(/(-?\d+\.\d+)%/)?.[1] || '0';

              setUnitDiscount(parseFloat(discount));
            }
          }
        }
      }
    } catch (error) {
      console.error('Error exporting visual data:', error);
    }
  };

  const updateOverallDiscount = async () => {
    if (!reportRef1.current) {
      console.error('Report container not found.');
      return;
    }

    const report = reportRef1.current;
    if (!report) {
      console.error('Report not found.');
      return;
    }

    try {
      const pages: Page[] = await report.getPages();

      let activePages = pages.filter((page: Page) => page.isActive);

      for (const page of activePages) {
        const visuals = await page.getVisuals();

        for (const visual of visuals) {
          if (visual.type === 'card') {
            if (visual.name === VISUAL_IDS.OVERALL_DISCOUNT) {
              let value = (await visual.exportData(models.ExportDataType.Summarized)).data;

              let overallDiscount = value.match(/(-?\d+\.\d+)%/)?.[1] || '0';

              setOverallDiscount(parseFloat(overallDiscount));
              setOverallDiscountLoading(false);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error exporting visual data:', error);
    }
  };

  const updateSaasFeeTotal = async () => {
    if (!reportRef1.current) {
      console.error('Report container not found.');
      return;
    }

    const report = reportRef1.current;
    if (!report) {
      console.error('Report not found.');
      return;
    }

    try {
      const pages: Page[] = await report.getPages();

      let activePages = pages.filter((page: Page) => page.isActive);

      for (const page of activePages) {
        const visuals = await page.getVisuals();

        for (const visual of visuals) {
          if (visual.name === VISUAL_IDS.SAAS_TOTAL_CARD) {
            let data = (await visual.exportData(models.ExportDataType.Summarized)).data;

            let saasTotal = parseFloat(data.trim().split('\r\n')[1].replace('$', ''));
            setSaasFeeTotal(saasTotal);
            setSaasFeeLoading(false);
          }
        }
      }
    } catch (error) {
      console.error('Error exporting SaaS fee total data:', error);
      setSaasFeeLoading(false);
    }
  };

  const refreshReport = async () => {
    try {
      if (reportRef1.current) {
        await reportRef1.current.refresh();
      }
    } catch (error) {
      console.error('Error refreshing report');
    }
    try {
      if (reportRef2.current) {
        await reportRef2.current.refresh();
      }
    } catch (error) {
      console.error('Error refreshing report');
    }
  };

  const updateSlicerState = async (
    tableName: string,
    columnName: string,
    slicerName1: string,
    slicerName2: string,
    value: any
  ) => {
    if (!reportRef1.current || !reportRef2.current) {
      console.error('Report container not found.');

      return;
    }

    const report1 = reportRef1.current;
    const report2 = reportRef2.current;

    if (!report1 || !report2) {
      console.error('Report container not found.');
      return;
    }

    const basicFilter: models.ISlicerFilter = {
      $schema: 'http://powerbi.com/product/schema#basic',
      filterType: 1,
      operator: 'In',
      requireSingleSelection: false,
      target: { table: tableName, column: columnName },
      values: [value],
    };

    try {
      const pages1: Page[] = await report1.getPages();
      const pages2: Page[] = await report2.getPages();

      let activePages1 = pages1.filter((page: Page) => page.isActive);
      let activePages2 = pages2.filter((page: Page) => page.isActive);

      const pages = [...activePages1, ...activePages2];

      for (const page of pages) {
        const visuals = await page.getVisuals();
        for (const visual of visuals) {
          if (visual.type === 'slicer' && (visual.name === slicerName1 || visual.name === slicerName2)) {
            try {
              visual.setSlicerState({
                filters: [basicFilter],
              });
            } catch (error) {
              console.error('Error setting slicer state:', error);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error exporting visual data:', error);
    }
  };

  const updateSeviceDiscountSlider = async (
    sliderName: string,
    sliceId: string,
    selection: number,
    setState: (value: number) => void
  ) => {
    const series = generateSeries(-1.0, 1.005, 0.005);

    setState(selection);

    var index = 200 - selection;

    await updateSlicerState(sliderName, sliderName, sliceId, sliceId, series[index]);
  };

  const setCompany = async (name: string) => {
    await updateSlicerState(
      TABLES.MARKET_UNIT_DISTRIBUTION,
      COLUMNS.COMPANY,
      VISUAL_IDS.COMPANY.REPORT1,
      VISUAL_IDS.COMPANY.REPORT2,
      name
    );

    setSelectedCompany(name);
    setInitialMarket(name);
  };

  const updateSlider = async (value: number) => {
    const start = 0;
    const end = 1.005;
    const step = 0.005;
    const series = generateSeries(start, end, step);
    await updateSlicerState(
      TABLES.EXTRA_DISCOUNT,
      COLUMNS.EXTRA_DISCOUNT,
      VISUAL_IDS.EXTRA_DISCOUNT.REPORT1,
      VISUAL_IDS.EXTRA_DISCOUNT.REPORT2,
      series[value]
    );

    setDiscountSliderOrder(value);
  };

  const updateSet = async (name: string) => {
    await updateSlicerState(
      TABLES.CALCULATION_METHOD,
      COLUMNS.STATUS,
      VISUAL_IDS.SET_ENABLED.REPORT1,
      VISUAL_IDS.SET_ENABLED.REPORT2,
      name === '' ? 'Off' : 'On'
    );
    await updateSlicerState(
      TABLES.CUSTOM_PRICE_VALUES,
      COLUMNS.SETNAME,
      VISUAL_IDS.SET_VALUE.REPORT1,
      VISUAL_IDS.SET_VALUE.REPORT2,
      name
    );

    setInitialSet(name);
    setSelectedSet(name);
  };

  const updatePaint = async (selection: string) => {
    await updateSlicerState(
      TABLES.PAINT_PROVIDED_TABLE,
      COLUMNS.PAINT_PROVIDED,
      VISUAL_IDS.PAINT_PROVIDED.REPORT1,
      VISUAL_IDS.PAINT_PROVIDED.REPORT2,
      selection
    );

    setPaintProvided(selection);
  };

  const updateDiscountToDisplay = async (selection: string) => {
    await updateSlicerState(
      TABLES.DISCOUNT_DISPLAY_OPTIONS,
      COLUMNS.OPTION,
      VISUAL_IDS.DISCOUNT_DISPLAY.REPORT1,
      VISUAL_IDS.DISCOUNT_DISPLAY.REPORT2,
      selection
    );

    setDiscountDisplay(selection);
  };

  const updateUnitDiscountSlider = async (selection: string) => {
    await updateSlicerState(
      TABLES.UNIT_DISCOUNT_OPTION,
      COLUMNS.OPTION,
      VISUAL_IDS.UNIT_DISCOUNT_ENABLED.REPORT1,
      VISUAL_IDS.UNIT_DISCOUNT_ENABLED.REPORT2,
      selection
    );

    setUnitDiscountOn(selection);
  };

  const updateDomainname = async (user: string) => {
    await updateSlicerState(
      TABLES.SYSTEMUSER_PARTITIONED,
      COLUMNS.DOMAINNAME,
      VISUAL_IDS.DOMAINNAME.REPORT1,
      VISUAL_IDS.DOMAINNAME.REPORT2,
      user
    );
  };

  const updatePaintSlider = async (selection: number) => {
    updateSeviceDiscountSlider(TABLES.PAINT_DISCOUNT, VISUAL_IDS.PAINT_DISCOUNT, selection, setPaintSliderOrder);
  };

  const updateCleaningSlider = async (selection: number) => {
    updateSeviceDiscountSlider(TABLES.CLEAN_DISCOUNT, VISUAL_IDS.CLEAN_DISCOUNT, selection, setCleaningSliderOrder);
  };

  const updateCarpetCleanSlider = async (selection: number) => {
    updateSeviceDiscountSlider(
      TABLES.CARPET_CLEAN_DISCOUNT,
      VISUAL_IDS.CARPET_CLEAN_DISCOUNT,
      selection,
      setCarpetCleanSliderOrder
    );
  };

  const updateMaintenanceSlider = async (selection: number) => {
    updateSeviceDiscountSlider(
      TABLES.MAINTENANCE_DISCOUNT,
      VISUAL_IDS.MAINTENANCE_DISCOUNT,
      selection,
      setMaintenanceSliderOrder
    );
  };

  const updateSaasFeeSlider = async (selection: number) => {
    updateSlicerState(
      TABLES.SAAS_UNIT_FEE,
      COLUMNS.SAAS_UNIT_FEE,
      VISUAL_IDS.SAAS_UNIT_FEE,
      VISUAL_IDS.SAAS_UNIT_FEE,
      generateSeries(0, 2.5, 0.01)[selection]
    );
    setSaasFeeSliderOrder(selection);
  };

  const updateSaasContractedUnits = async (selection: number) => {
    updateSlicerState(
      TABLES.SAAS_UNIT_COUNT,
      COLUMNS.SAAS_UNIT_COUNT,
      VISUAL_IDS.SAAS_UNIT_COUNT,
      VISUAL_IDS.SAAS_UNIT_COUNT,
      selection
    );
    setSaasContractedUnits(selection);
  };

  const updateSaasFeePeriod = async (selection: string) => {
    await updateSlicerState(
      TABLES.SAAS_BILLING_CYCLE,
      COLUMNS.VALUE,
      VISUAL_IDS.SAAS_BILLING_CYCLE,
      VISUAL_IDS.SAAS_BILLING_CYCLE,
      selection
    );
    setSaasFeePeriod(selection as 'Monthly' | 'Annually');
  };

  const updateSaasFeeEnabled = async (selection: string) => {
    await updateSlicerState(
      TABLES.SAAS_FEE_ENABLED,
      COLUMNS.STATUS,
      VISUAL_IDS.SAAS_FEE_ENABLED.REPORT1,
      VISUAL_IDS.SAAS_FEE_ENABLED.REPORT2,
      selection
    );
    setSaasFeeEnabled(selection as 'Yes' | 'No');
  };

  const canExport = () => {
    if (isAdminUser()) {
      return true;
    }
    if (isCROUser()) {
      return overallDiscountLoading === false && overallDiscount >= minDiscount;
    }
    return false;
  };

  return (
    <div className="App">
      <Header />
      {error.length > 0 ? (
        <ErrorDisplay errors={error} />
      ) : username ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-around',
            backgroundColor: '#101D3B0D',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'start',
              alignItems: 'start',
              padding: '1.6vh 2.4vh 1.6vh 2.4vh',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-around',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'space-around',
                  flexDirection: 'row',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'start',
                    alignItems: 'baseline',
                    gap: '0.8vh',
                    width: '32.4vh',
                  }}
                >
                  <div
                    style={{
                      fontSize: '1.4vh',
                      lineHeight: '1.6vh',
                      fontWeight: '700',
                      color: '#101D3B',
                    }}
                  >
                    Company
                  </div>
                  <MarketEditor
                    setCompanyNames={setCompanyNames}
                    setExternalCompanies={setMarkets}
                    refreshReport={refreshReport}
                    companyNames={companyNames}
                    reportRef1={reportRef1}
                    reportRef2={reportRef2}
                    setExternalSelected={setSelectedCompany}
                    selectedCompany={initialMarket}
                  />
                  <NotesComponent note={note} setNote={setNote} />
                  {canDiscount() && (
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'row', gap: '0.8vh' }}>
                      <div
                        style={{
                          width: '100%',
                          height: '5vh',
                          backgroundColor: '#ffffff',
                          borderRadius: '1.6vh',
                          boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
                          padding: '0vh 1.6vh',
                          boxSizing: 'border-box',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <Margin margin={margin} isLoading={marginLoading} />
                      </div>
                      <div
                        style={{
                          width: '100%',
                          height: '5vh',
                          backgroundColor: '#ffffff',
                          borderRadius: '1.6vh',
                          boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
                          padding: '0vh 1.6vh',
                          boxSizing: 'border-box',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <OverallDiscount
                          overallDiscount={overallDiscount}
                          isLoading={overallDiscountLoading}
                          minDiscount={minDiscount}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                    gap: '0.8vh',
                    flexDirection: 'column',
                    marginTop: '2.4vh',
                    marginLeft: '0.8vh',
                  }}
                >
                  {canDiscount() && (
                    <>
                      <div
                        style={{
                          width: '30vh',
                          backgroundColor: '#ffffff',
                          borderRadius: '1.6vh',
                          boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-around',
                          alignItems: 'start',
                          padding: '0 1.6vh',
                          boxSizing: 'border-box',
                        }}
                      >
                        {/* empty element for spacing */}
                        <div></div>
                        <OmniSlider setOrder={updatePaintSlider} initialOrder={paintSliderOrder} title="Paint" />
                        <div style={styles.separator} />
                        <OmniSlider setOrder={updateCleaningSlider} initialOrder={cleaningSliderOrder} title="Clean" />
                        <div style={styles.separator} />
                        <OmniSlider
                          setOrder={updateCarpetCleanSlider}
                          initialOrder={carpetCleanSliderOrder}
                          title="Carpet Clean"
                        />
                        <div style={styles.separator} />
                        <OmniSlider
                          setOrder={updateMaintenanceSlider}
                          initialOrder={maintenanceSliderOrder}
                          title="Maintenance"
                        />
                        <div style={styles.separator} />
                        <PaintPicker
                          reportRef1={reportRef1}
                          reportRef2={reportRef2}
                          setExternalPaintProvided={setPaintProvided}
                          isOn={paintProvided === 'Yes'}
                        />
                        {/* empty element for spacing */}
                        <div></div>
                      </div>
                    </>
                  )}
                  {!canDiscount() && (
                    <>
                      <div
                        style={{
                          width: '100%',
                          height: '5vh',
                          backgroundColor: '#ffffff',
                          borderRadius: '1.6vh',
                          boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
                          padding: '0vh 1.6vh',
                          boxSizing: 'border-box',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <Margin margin={margin} isLoading={marginLoading} />
                      </div>

                      <div
                        style={{
                          width: '100%',
                          height: '5vh',
                          backgroundColor: '#ffffff',
                          borderRadius: '1.6vh',
                          boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
                          padding: '0vh 1.6vh',
                          boxSizing: 'border-box',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <OverallDiscount
                          overallDiscount={overallDiscount}
                          isLoading={overallDiscountLoading}
                          minDiscount={minDiscount}
                        />
                      </div>

                      <div
                        style={{
                          width: '100%',
                          height: '5vh',
                          backgroundColor: '#ffffff',
                          borderRadius: '1.6vh',
                          boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
                          padding: '0vh 1.6vh',
                          boxSizing: 'border-box',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <PaintPicker
                          reportRef1={reportRef1}
                          reportRef2={reportRef2}
                          setExternalPaintProvided={setPaintProvided}
                          isOn={paintProvided === 'Yes'}
                        />
                      </div>
                    </>
                  )}
                  <PlatformFee
                    onBillingCycleChange={async (value: string) => {
                      await updateSaasFeePeriod(value);
                    }}
                    onFeePerUnitChange={async (value: number) => {
                      await updateSaasFeeSlider(value);
                    }}
                    onUnitCountChange={async (value: number) => {
                      await updateSaasContractedUnits(value);
                    }}
                    onSaasFeeEnabledChange={async (value: string) => {
                      await updateSaasFeeEnabled(value);
                    }}
                    initialFeePerUnit={saasFeeSliderOrder}
                    initialUnitCount={saasContractedUnits}
                    initialBillingCycle={saasFeePeriod}
                    initialSaasFeeEnabled={saasFeeEnabled}
                    isLoadingTotal={saasFeeLoading}
                    total={saasFeeTotal}
                  />
                </div>
              </div>
            </div>
            <div style={{ height: '1.6vh' }} />
            <div
              style={{
                width: '63.2vh',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'start',
                alignItems: 'baseline',
              }}
            >
              <div
                style={{
                  fontSize: '1.4vh',
                  lineHeight: '1.6vh',
                  fontWeight: '700',
                  color: '#101D3B',
                }}
              >
                Adjust Set
              </div>
              <div style={{ height: '0.8vh' }} />
              <AdjustmentsEditor
                setExternalSetNames={setSetNames}
                setExternalAdjustments={setAdjustments}
                refreshReport={refreshReport}
                reportRef1={reportRef1}
                reportRef2={reportRef2}
                setNames={setNames}
                setExternalSelected={setSelectedSet}
                selectedSet={initialSet}
              />
            </div>
          </div>
          <div
            style={{
              flex: 2,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '4vh 2.4vh 0vh 0vh',
            }}
          >
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-around',
                width: '100%',
              }}
            >
              <PowerBIReport
                embedUrl={embedUrl}
                accessToken={accessToken}
                reportRef={reportRef1}
                onRender={onReportRendered}
                username={username}
                onLoad={() => {
                  setReport1Loaded(true);
                }}
              />
              <div style={{ width: '1.6vh' }} />
              <PowerBIReport
                embedUrl={embedUrl}
                accessToken={accessToken}
                reportRef={reportRef2}
                initPage={1}
                username={username}
                onLoad={() => {
                  setReport2Loaded(true);
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                height: '8.8vh',
                width: '100%',
                backgroundColor: '#fff',
                borderRadius: '1.6vh 1.6vh 0vh 0vh',
              }}
            >
              {canExport() && (
                <>
                  <ExcelExportButton
                    reportRef={reportRef1}
                    discountDisplay={discountDisplay}
                    paintProvided={paintProvided}
                    selectedSet={selectedSet}
                    exportName={selectedCompany}
                    selectedCompany={selectedCompany}
                    markets={markets.filter(market => market.company === selectedCompany)}
                    adjustments={adjustments.filter(adjustment => adjustment.setname === selectedSet)}
                  />
                  <div style={{ width: '1.6vh' }} />
                  <PDFExportButton
                    reportRef1={reportRef1}
                    reportRef2={reportRef2}
                    paintProvided={paintProvided === 'Yes'}
                    exportName={selectedCompany}
                  />
                  <div style={{ width: '1.6vh' }} />
                </>
              )}
              {!id && (
                <SavePresetButton
                  companyName={selectedCompany}
                  discountDisplay={discountDisplay}
                  paintProvided={paintProvided}
                  extraDiscount={discountSliderOrder}
                  setName={selectedSet}
                  unitDiscountEnabled={unitDiscountOn}
                  unitDiscount={unitDiscount}
                  margin={margin}
                  overallDiscount={overallDiscount}
                  paintDiscount={paintSliderOrder}
                  cleanDiscount={cleaningSliderOrder}
                  carpetCleanDiscount={carpetCleanSliderOrder}
                  maintenanceDiscount={maintenanceSliderOrder}
                  minDiscount={minDiscount}
                  saasUnitFee={saasFeeSliderOrder}
                  saasContractedUnits={saasContractedUnits}
                  saasFeePeriod={saasFeePeriod}
                  saasPeriodCost={saasFeeTotal}
                  saasFeeEnabled={saasFeeEnabled}
                  notes={note}
                />
              )}
              {id && (
                <>
                  <SavePresetButton
                    companyName={selectedCompany}
                    discountDisplay={discountDisplay}
                    paintProvided={paintProvided}
                    extraDiscount={discountSliderOrder}
                    setName={selectedSet}
                    unitDiscountEnabled={unitDiscountOn}
                    unitDiscount={unitDiscount}
                    margin={margin}
                    overallDiscount={overallDiscount}
                    paintDiscount={paintSliderOrder}
                    cleanDiscount={cleaningSliderOrder}
                    carpetCleanDiscount={carpetCleanSliderOrder}
                    maintenanceDiscount={maintenanceSliderOrder}
                    minDiscount={minDiscount}
                    saasUnitFee={saasFeeSliderOrder}
                    saasContractedUnits={saasContractedUnits}
                    saasFeePeriod={saasFeePeriod}
                    saasPeriodCost={saasFeeTotal}
                    saasFeeEnabled={saasFeeEnabled}
                    notes={note}
                    originalName={preset?.name}
                  />
                  <div style={{ width: '1.6vh' }} />
                  <SavePresetButton
                    companyName={selectedCompany}
                    discountDisplay={discountDisplay}
                    paintProvided={paintProvided}
                    extraDiscount={discountSliderOrder}
                    setName={selectedSet}
                    unitDiscountEnabled={unitDiscountOn}
                    unitDiscount={unitDiscount}
                    margin={margin}
                    overallDiscount={overallDiscount}
                    paintDiscount={paintSliderOrder}
                    cleanDiscount={cleaningSliderOrder}
                    carpetCleanDiscount={carpetCleanSliderOrder}
                    maintenanceDiscount={maintenanceSliderOrder}
                    minDiscount={minDiscount}
                    saasUnitFee={saasFeeSliderOrder}
                    saasContractedUnits={saasContractedUnits}
                    saasFeePeriod={saasFeePeriod}
                    saasPeriodCost={saasFeeTotal}
                    saasFeeEnabled={saasFeeEnabled}
                    notes={note}
                    originalId={parseInt(id)}
                    originalName={preset?.name}
                  />
                </>
              )}

              {/* for report debugging */}
              {/* <button onClick={logReportState}>Log Report State</button> */}
            </div>
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

const styles = {
  separator: {
    width: '100%',
    backgroundColor: 'rgba(16, 29, 59, 0.1)',
    height: '0.1vh',
    margin: '0 -1.6vh',
    padding: '0 1.6vh',
  },
};

export default MainPage;
